import {
  type ComponentProps,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useState,
} from 'react'
import { useIntersection } from '@mantine/hooks'
import Text from 'src/components/ui/Text'
import { cx } from 'src/utils/cx'
import FluentChevronDownUp16Filled from '~icons/fluent/chevron-down-up-16-filled'
import FluentChevronUpDown16Filled from '~icons/fluent/chevron-up-down-16-filled'

/**
 * A content box that can be expanded to show more content.
 *
 * This component doesn't handle height changes.
 * You need to manually set the height via, e.g., max-height.
 */
export function ExpandableContentBox(
  props: ComponentProps<'div'> & {
    toggle?: (state: {
      expanded: boolean
      setExpanded: Dispatch<SetStateAction<boolean>>
    }) => ReactNode
  },
) {
  const { toggle, ...domProps } = props

  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const { ref: contentRef, entry: intersection } = useIntersection({
    root: container,
  })
  const overflowed = Number(intersection?.intersectionRatio) < 1

  const [expanded, _setExpanded] = useState(false)
  const setExpanded: Dispatch<SetStateAction<boolean>> = input => {
    let resolved: boolean
    _setExpanded(value => {
      resolved = typeof input === 'function' ? input(value) : input
      return resolved
    })
    setTimeout(() => {
      container?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }
  const expandable = expanded || overflowed
  const [key, setKey] = useState(Math.random())

  // When content changes, we want to reset the whole component state
  useEffect(() => {
    _setExpanded(false)
    setKey(Math.random())
  }, [props.children])

  return (
    <div
      {...domProps}
      data-expandable={expandable ? '' : null}
      data-expanded={expanded ? '' : null}
      className={cx('flex flex-col', props.className)}
    >
      <div
        ref={setContainer}
        className={cx(
          'flex-1 overflow-hidden',
          expandable &&
            !expanded &&
            '[mask:linear-gradient(to_top,#0000,#3333_10px,#fff_20px)]',
        )}
      >
        <div key={key} ref={contentRef}>
          {props.children}
        </div>
      </div>
      {expandable && (
        <div className="flex-none">
          {toggle ? (
            toggle({ expanded, setExpanded })
          ) : (
            <button
              className={cx(
                'flex items-center gap-0.5 h-7 px-2 text-sm rounded-full',
                'hover:bg-white ring-1 ring-black/15',
                'dark:hover:text-white dark:hover:bg-white/20 dark:ring-white/15',
              )}
              onClick={() => setExpanded(val => !val)}
            >
              {expanded ? (
                <>
                  <FluentChevronDownUp16Filled className="size-4 -ml-1" />
                  <Text i18nKey="artwork:show.less" />
                </>
              ) : (
                <>
                  <FluentChevronUpDown16Filled className="size-4 -ml-1" />
                  <Text i18nKey="artwork:show.more" />
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
